
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LANG_LABELS = {
  en: "English",
  al: "Shqip",
  it: "Italiano",
  es: "Español",
};

// Official FrontSpace PNG logo
const LOGO_PATH = "/LogoFrontSpace.png";

export default function Navbar() {
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    onScroll();

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const links = [
    { key: "home", path: "/" },
    { key: "about", path: "/aboutus" },
    { key: "services", path: "/services" },
    { key: "news", path: "/news" },
    { key: "contact", path: "/contact" },
  ];

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path ||
        location.pathname.startsWith(path + "/");

  const getLinkLabel = (key) => {
    if (key === "services") {
      return "Services";
    }

    if (key === "about") {
      return t.nav?.about || "About Us";
    }

    return t.nav?.[key] || key;
  };

  const closeMenus = () => {
    setMobileOpen(false);
    setLangOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-[#050814]/95 backdrop-blur-xl border-b border-[#00F0FF]/10 shadow-lg"
          : "bg-[#050814]/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">

        {/* FRONTSPACE LOGO - CLICK TO RETURN HOME */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center flex-shrink-0"
          aria-label="FrontSpace - Go to homepage"
          onClick={closeMenus}
        >
          <img
            src={LOGO_PATH}
            alt="FrontSpace Logo"
            className="h-12 sm:h-14 w-auto max-w-[180px] object-contain"
          />
        </Link>

        {/* DESKTOP NAVIGATION */}
        <div className="hidden lg:flex items-center justify-center gap-5 xl:gap-7">
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              data-testid={`nav-${link.key}`}
              className={`text-sm font-medium transition-all duration-200 hover:text-[#00F0FF] relative whitespace-nowrap ${
                isActive(link.path)
                  ? "text-[#00F0FF]"
                  : "text-slate-300"
              }`}
            >
              {getLinkLabel(link.key)}

              {isActive(link.path) && (
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#00F0FF]" />
              )}
            </Link>
          ))}
        </div>

        {/* RIGHT SIDE CONTROLS */}
        <div className="flex items-center gap-3 flex-shrink-0">

          {/* LANGUAGE SELECTOR */}
          <div className="relative">
            <button
              type="button"
              data-testid="lang-selector-btn"
              aria-label="Select language"
              aria-expanded={langOpen}
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-slate-300 hover:text-[#00F0FF] transition-colors text-sm px-2 py-1 border border-white/10 hover:border-[#00F0FF]/30 rounded"
            >
              <Globe size={13} />

              <span className="terminal text-xs">
                {language.toUpperCase()}
              </span>

              <ChevronDown
                size={11}
                className={`transition-transform ${
                  langOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {langOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-[#0A0F2E] border border-[#00F0FF]/20 rounded-md overflow-hidden shadow-2xl z-50">
                {["en", "al", "it", "es"].map((code) => (
                  <button
                    key={code}
                    type="button"
                    data-testid={`lang-option-${code}`}
                    onClick={() => {
                      setLanguage(code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-[#00F0FF]/10 ${
                      language === code
                        ? "text-[#00F0FF]"
                        : "text-slate-300"
                    }`}
                  >
                    {LANG_LABELS[code]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* MOBILE MENU BUTTON */}
          <button
            type="button"
            data-testid="mobile-menu-btn"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className="lg:hidden text-white p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* MOBILE NAVIGATION */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#050814]/95 backdrop-blur-xl border-t border-[#00F0FF]/10 px-6 py-4">
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              data-testid={`mobile-nav-${link.key}`}
              onClick={closeMenus}
              className={`block py-3 text-sm border-b border-white/5 transition-colors ${
                isActive(link.path)
                  ? "text-[#00F0FF]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              {getLinkLabel(link.key)}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
